// Accounts and access (M8): who a user is, which servers they can reach, and invite links.
// Admins aren't recorded here: admin is the Cognito `admin` group, read from the caller's token.

import type { BillingStatus } from './billing.js';

/** Servers a user may own (not counting destroyed ones) unless their record allows more. */
export const DEFAULT_SERVER_LIMIT = 5;

/**
 * The env's server cap (owner, 2026-10-27): servers not destroyed, on the whole env, before creating
 * is refused (admins pass). Kept in the Settings table under SERVER_CAP_SETTING; no row = the env's
 * default (infra config). 0 pauses new servers.
 */
export const SERVER_CAP_SETTING = 'serverCap';
export const SERVER_CAP_MAX = 1000;
/** What someone creating is told when the env is at its cap (owner's wording, 2026-10-27). */
export const SERVER_CAP_REFUSAL = 'Hearth is at max capacity right now. Please try again later.';

/** The server cap as set by hand on the admin page. */
export interface ServerCapSetting {
  value: number;
  /** The admin who set it (their user ID). */
  setBy: string;
  setAt: string; // ISO 8601 UTC
}

/**
 * The servers a user may own at once: their record's limit, never below the default (records made
 * while it was 3 keep 3, as `serverLimit` is written only at first sign-in).
 */
export function serverLimitOf(user: Pick<UserRecord, 'serverLimit'>): number {
  return Math.max(user.serverLimit, DEFAULT_SERVER_LIMIT);
}

/** How long an invite link works. */
export const INVITE_TTL_DAYS = 7;

/** GSI on `Servers`: `ownerId` + `createdAt`, projecting `status`, for counting a user's servers. */
export const SERVERS_BY_OWNER_INDEX = 'byOwner';

/** GSI on `ServerAccess`: `serverId` + `userId`, for a server's owner and members. */
export const ACCESS_BY_SERVER_INDEX = 'byServer';

/** GSI on `Users`, `discordUserId` only (sparse: users with one), for the Discord bot (M14) finding the Hearth user who ran a command. */
export const USERS_BY_DISCORD_INDEX = 'byDiscord';

/** GSI on `Invites`: `serverId` + `createdAt`, for a server's invite links. */
export const INVITES_BY_SERVER_INDEX = 'byServer';

/** An item in the `Users` table, keyed by the Cognito user's `sub`. */
export interface UserRecord {
  userId: string;
  /** Servers they may own at once (default DEFAULT_SERVER_LIMIT; read it through serverLimitOf). */
  serverLimit: number;
  createdAt: string; // ISO 8601 UTC: first seen
  lastSeenAt: string; // ISO 8601 UTC: last profile refresh
  // Profile, refreshed from the sign-in token; which fields arrive depends on the provider.
  provider?: string; // 'Google', 'Discord', or 'Cognito' (a password user)
  email?: string;
  name?: string; // Google
  username?: string; // Discord
  displayName?: string; // Discord
  picture?: string; // avatar URL
  /**
   * Their Discord user ID (M14): from a Discord sign-in, or (14.2) connected to an account signed in
   * with Google. A Google sign-in never removes it. What the Discord bot knows them by.
   */
  discordUserId?: string;
  /** Their Discord username, saved when they connected Discord (14.2); a Discord sign-in has `username`. */
  discordUsername?: string;
  /** Their Stripe Customer (billing): made when they first add a card, never changed; the card is on it. */
  stripeCustomerId?: string;
  /** Absent is fine; `past_due` blocks waking and creating until the unpaid invoice is paid. */
  billingStatus?: BillingStatus;
  /** The Terms they last agreed to (TERMS_VERSION), saying they're 18 or older; needed for a card or a server. */
  termsVersion?: string;
  termsAcceptedAt?: string; // ISO 8601 UTC
}

/** The profile fields of a user, as read from their token. */
export type UserProfile = Pick<UserRecord, 'provider' | 'email' | 'name' | 'username' | 'displayName' | 'picture' | 'discordUserId'>;

/** What a user is to a server. Admins reach every server without an access item. */
export type ServerRole = 'owner' | 'member';

/** An item in the `ServerAccess` table: one user's access to one server. */
export interface ServerAccessRecord {
  userId: string;
  serverId: string;
  role: ServerRole;
  addedAt: string; // ISO 8601 UTC
  /** Who granted it: for a member, the invite's creator; for the owner, themselves (at create). */
  addedBy: string;
  /** The invite it came through, for members. */
  inviteCode?: string;
}

/** An item in the `Invites` table: a link that makes whoever accepts it a member of a server. */
export interface InviteRecord {
  /** 20 Crockford base32 characters (100 random bits), stored without dashes. */
  code: string;
  serverId: string;
  createdBy: string;
  createdAt: string; // ISO 8601 UTC
  expiresAt: string; // ISO 8601 UTC; checked on every read
  /** The same moment in epoch seconds: DynamoDB's TTL deletes the item some time after it. */
  expiresAtEpoch: number;
}

/** DiscordLinks' index by Hearth server: the Discord servers a server is linked to (M14.7's announcements). */
export const DISCORD_LINKS_BY_SERVER_INDEX = 'byServer';

/**
 * A Hearth server linked to a Discord server (M14.5, `/hearth link`): people there may wake, sleep
 * and check it with `/hearth`, if they're on it in Hearth. One row per Discord server and Hearth server.
 */
export interface DiscordLinkRecord {
  /** The Discord server (Discord calls it a guild). */
  guildId: string;
  serverId: string;
  /** Where `link` was used: announcements go there. */
  channelId: string;
  /** The Hearth user (the server's owner) who linked it. */
  linkedBy: string;
  linkedAt: string; // ISO 8601 UTC
  /** The latest `/hearth wake` reply here, edited once the server is up or has failed (M14.7). */
  wakeReply?: WakeReply;
}

/**
 * What it takes to edit a `/hearth wake` reply: Discord lets an app edit its answer to an interaction
 * through the interaction's token for 15 minutes. Kept only until used or expired.
 */
export interface WakeReply {
  applicationId: string;
  token: string;
  /** Who woke it, for "@Sam woke …". */
  discordId: string;
  expiresAt: string; // ISO 8601 UTC
}
